import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGlowIntersection } from '../../gamer/hooks/useGlowIntersection'
import { DEFAULT_FADE_DURATION, DEFAULT_GLINT_DURATION, loadGlintDurationConfig } from '../constants/glintConfig'

export type GlintPhase = 'idle' | 'running'

type OverlayState = 'hidden' | 'blur' | 'fade'

export interface GlintRevealCoverLayers {
  isCovered: boolean
  isOverlayActive: boolean
  overlayState: OverlayState
  glintPhase: GlintPhase
  glintTrigger: number
  glintDurationSec: number
  fadeDurationSec: number
  onGlintComplete: () => void
}

export interface UseGlintRevealCoverParams {
  /** The box stays frosted until the pointer reaches it. */
  isCovered: boolean
  /** Called once the cover has faded away. */
  onRevealed?: () => void
  onGlintStateChange?: (state: GlintPhase) => void
}

/** A feedback box hidden under a frosted cover that a glint wipes away when the pointer reaches it. */
export const useGlintRevealCover = ({ isCovered, onRevealed, onGlintStateChange }: UseGlintRevealCoverParams) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [glintTrigger, setGlintTrigger] = useState(0)
  const [isOverlayActive, setIsOverlayActive] = useState(isCovered)
  const [overlayState, setOverlayState] = useState<OverlayState>(isCovered ? 'blur' : 'hidden')
  const [glintPhase, setGlintPhase] = useState<GlintPhase>('idle')
  const hasRevealedRef = useRef(false)
  const glintCompletedRef = useRef(!isCovered)
  const lastTriggerRef = useRef(glintTrigger)
  const fadeStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const overlayHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const storedDurations = useMemo(() => loadGlintDurationConfig(), [])
  const glintDurationSec = storedDurations.glintDuration ?? DEFAULT_GLINT_DURATION
  const fadeDurationSec = storedDurations.fadeDuration ?? DEFAULT_FADE_DURATION
  const experienceDurationSec = storedDurations.experienceDuration ?? glintDurationSec + fadeDurationSec

  useEffect(() => {
    if (isCovered) {
      setIsOverlayActive(true)
      setOverlayState('blur')
      setGlintPhase('idle')
      glintCompletedRef.current = false
      hasRevealedRef.current = false
      return
    }

    setIsOverlayActive(false)
    setOverlayState('hidden')
    setGlintPhase('idle')
    glintCompletedRef.current = true
    if (overlayHideTimeoutRef.current) {
      clearTimeout(overlayHideTimeoutRef.current)
      overlayHideTimeoutRef.current = null
    }
    if (fadeStartTimeoutRef.current) {
      clearTimeout(fadeStartTimeoutRef.current)
      fadeStartTimeoutRef.current = null
    }
    lastTriggerRef.current = glintTrigger
  }, [isCovered, glintTrigger])

  useEffect(() => {
    if (!isCovered) return
    if (glintTrigger === 0 || glintTrigger === lastTriggerRef.current) return

    setIsOverlayActive(true)
    setOverlayState('blur')
    setGlintPhase('running')
    glintCompletedRef.current = false
    if (overlayHideTimeoutRef.current) {
      clearTimeout(overlayHideTimeoutRef.current)
      overlayHideTimeoutRef.current = null
    }
    if (fadeStartTimeoutRef.current) {
      clearTimeout(fadeStartTimeoutRef.current)
      fadeStartTimeoutRef.current = null
    }

    const fadeStartDelaySec = Math.max(experienceDurationSec - fadeDurationSec, 0)
    const handleFadeStart = () => {
      fadeStartTimeoutRef.current = null
      setOverlayState('fade')
      if (fadeDurationSec <= 0) {
        setOverlayState('hidden')
        setIsOverlayActive(false)
        onRevealed?.()
        return
      }
      overlayHideTimeoutRef.current = setTimeout(() => {
        setOverlayState('hidden')
        setIsOverlayActive(false)
        overlayHideTimeoutRef.current = null
        onRevealed?.()
      }, fadeDurationSec * 1000)
    }

    if (fadeStartDelaySec <= 0) {
      handleFadeStart()
    } else {
      fadeStartTimeoutRef.current = setTimeout(handleFadeStart, fadeStartDelaySec * 1000)
    }
    lastTriggerRef.current = glintTrigger
  }, [experienceDurationSec, fadeDurationSec, glintTrigger, onRevealed, isCovered])

  useEffect(() => {
    return () => {
      glintCompletedRef.current = true
      if (overlayHideTimeoutRef.current) {
        clearTimeout(overlayHideTimeoutRef.current)
      }
      if (fadeStartTimeoutRef.current) {
        clearTimeout(fadeStartTimeoutRef.current)
      }
    }
  }, [])

  const handleGlintComplete = useCallback(() => {
    if (glintCompletedRef.current) return
    glintCompletedRef.current = true
    setGlintPhase('idle')
  }, [])

  useEffect(() => {
    onGlintStateChange?.(glintPhase)
  }, [glintPhase, onGlintStateChange])

  useGlowIntersection(containerRef, {
    enabled: isCovered,
    onEnter: () => {
      if (!hasRevealedRef.current && glintPhase !== 'running') {
        setGlintTrigger((prev) => prev + 1)
      }
    }
  })

  // The pointer or keyboard focus reaching the box starts the glint.
  const startReveal = useCallback(() => {
    if (isCovered && !hasRevealedRef.current && glintPhase !== 'running') {
      setGlintTrigger((prev) => prev + 1)
    }
  }, [isCovered, glintPhase])

  const layers: GlintRevealCoverLayers = {
    isCovered,
    isOverlayActive,
    overlayState,
    glintPhase,
    glintTrigger,
    glintDurationSec,
    fadeDurationSec,
    onGlintComplete: handleGlintComplete
  }

  return { containerRef, startReveal, layers }
}
